import VueMask from 'di-vue-mask';
import ElementUI from 'element-ui';
import locale from 'element-ui/lib/locale';
import lang from 'element-ui/lib/locale/lang/en';
import 'element-ui/lib/theme-chalk/index.css';
import * as Logger from 'js-logger';
import { isNil } from 'lodash-es';
import qs from 'qs';
import VTooltip from 'v-tooltip';
import VeeValidate from 'vee-validate';
import Vue from 'vue';
import VModal from 'vue-js-modal';
import { Component } from 'vue-property-decorator';
import VueScrollTo from 'vue-scrollto';
import VueVisible from 'vue-visible';
import filters from '../src/filters';
import Foot from './components/foot';
import MessageModel from './components/messageModel';
import Navbar from './components/navbar';
import Sidebar from './components/sidebar';
import Config from './config.json';
import { createAnalyticsDirectives } from './directives/analytics';
import RxEvent from './directives/rx-event';
import './localisation';
import template from './main.vue';
import './polyfill';
import router from './router';
import tmsService from './services/axios/tmsService';
import tmsTransformRequestService from './services/axios/tmsTransformRequestService';
import { CK_DEBT_EMAIL, LocalStorageCache } from './services/cache';
import { TMSCustomerAccountService } from './services/user';
import { analytics } from './services/analytics';
import PluginHead from './shared/popups';
import store from './store';
import './style.scss';
import veeConfig from './validation';
import customValidationRules from './validation/customRules';
import { Message } from 'element-ui';

// Configure ElementUI with English locale
locale.use(lang);
Vue.use(ElementUI, { locale: lang });

let logLevel = Config.debug ? Logger.DEBUG : Logger.ERROR;
Logger.useDefaults();
Logger.setLevel(logLevel);

Vue.config.errorHandler = function(err, vm, info) {
    Logger.error('Vue error: ', err);
};

// common functions
Vue.prototype.goToPage = function(link: any) {
    const router = this.$router;
    const current = router.currentRoute;
    if (
        link === '/invoices' &&
        current.name === 'Invoices' &&
        /referer=bnp_debt_email/gi.test(current.fullPath)
    ) {
        router.replace({
            name: 'Invoices',
            params: {},
            query: { forceReload: 1 },
        });
        return;
    }

    router.push(link);
};

// router middleware
router.beforeEach(async function(to, from, next) {
    // redirect http to https
    if (window.location.hostname === 'ship.unisco.com' && window.location.protocol !== 'https:') {
        window.location.href = 'https://ship.unisco.com/v2';
    } else if (
        window.location.hostname === 'demo.unisco.com' &&
        window.location.protocol !== 'https:'
    ) {
        window.location.href = 'https://demo.unisco.com/v2';
    }

    if (isNil(to.name)) {
        return;
    }

    const href = location.href;
    const anonymousPages = new Set([
        'Login',
        'TrackV2',
        'TrackShipmentGuest',
        'TrackingGuest',
        'VerifyEmail',
        'ShipmentView',
        'ClientSsoLogin',
    ]);
    const needsSignIn = !anonymousPages.has(to.name);
    const isGuest = isNil(localStorage.getItem('UserID'));

    type BNPDebtEmailParameters = {
        billToName: string;
        referer: string;
    };

    const isGoingToInvoices = () => {
        return to.name === 'Invoices';
    };

    const isBNPDebtEmail = () => {
        if (isGoingToInvoices() && href.indexOf('?') > -1) {
            const query: BNPDebtEmailParameters = qs.parse(href.substring(href.indexOf('?') + 1));
            if (query.referer === 'bnp_debt_email') {
                return true;
            }
        }

        return false;
    };

    const ensureBillTo = async (debtEmail: BNPDebtEmailParameters) => {
        const service = TMSCustomerAccountService.create({
            cache: LocalStorageCache.create(),
            tmsService: tmsService,
            tmsTransformRequestService: tmsTransformRequestService,
        });

        if (service.billToList.length <= 1) {
            return;
        }

        const currentBillTo = service.currentBillTo;
        const currentBillToName = currentBillTo ? currentBillTo.location_name : null;
        if (currentBillToName === debtEmail.billToName) {
            return;
        }

        const chosen = service.findBillTo(i => i.location_name === debtEmail.billToName);
        if (chosen) {
            await service.useBillTo(chosen);
        }
    };

    /**
     * [CRM-148] Milo
     *
     * ## procedure
     *
     * 1. Customer click debt collection email and go to paymen
     * 2. Check if customer is signed in
     *   2.1. If customer is signed in then go to invoices page
     *   2.2. If customer is not signed in, do:
     *     2.2.1 cache debt email parameters
     *     2.2.2 redirect to sign in page, then ask customer to sign in
     *     2.2.3 once customer gets sign in, get cached parameters then redirect to invoices page
     */
    if (isGuest) {
        if (isBNPDebtEmail()) {
            localStorage.setItem(CK_DEBT_EMAIL, href.substring(href.indexOf('?') + 1));
        }

        if (needsSignIn) {
            next('/');
        } else {
            next();
        }

        return;
    }

    if (isBNPDebtEmail()) {
        const params: BNPDebtEmailParameters = qs.parse(href.substring(href.indexOf('?') + 1));
        await ensureBillTo(params);
        next();
        return;
    }

    /**
     * check 2.2.3 above
     */
    const bnpDebtEmailParams = localStorage.getItem(CK_DEBT_EMAIL);
    if (bnpDebtEmailParams) {
        const params: BNPDebtEmailParameters = qs.parse(bnpDebtEmailParams);
        await ensureBillTo(params);
        localStorage.removeItem(CK_DEBT_EMAIL);
        next({
            name: 'Invoices',
            query: { ...params },
        });
        return;
    }

    const isShipperOrConsignee = localStorage.getItem('IsShipperOrConsignee') == '1';
    let isSwitchToClientPortal = localStorage.getItem('switchToClientPortal') == '1';
    const is_invalid_to =
        to.path !== '/shipments-4-shippers' &&
        to.path !== '/pickupDashboard' &&
        to.name !== 'shipments-4-shippers-order-details' &&
        to.name !== 'shipments-4-shippers-shipment-view';
    const is_valid_from =
        from.path === '/shipments-4-shippers' ||
        from.name === 'shipments-4-shippers-order-details' ||
        from.path === '/pickupDashboard';

    if (isShipperOrConsignee && is_valid_from && is_invalid_to && !isSwitchToClientPortal) {
        if (from.path === '/shipments-4-shippers') {
            next({
                name: 'shipments-4-shippers',
            });
            return;
        }

        if (from.name === 'shipments-4-shippers-order-details') {
            next({
                name: 'shipments-4-shippers-order-details',
            });
            return;
        }

        if (from.path === '/pickupDashboard') {
            next({
                path: '/pickupDashboard',
            });
            return;
        }
    }
    if (isShipperOrConsignee && !isSwitchToClientPortal && is_invalid_to) {
        next({
            name: 'shipments-4-shippers',
        });
        return;
    }

    next();
});

analytics.init();
analytics.restoreUserContext();
router.afterEach(to => analytics.pageView(to));
const analyticsDirectives = createAnalyticsDirectives(analytics);
Vue.directive('analytics-form', analyticsDirectives.form);
Vue.directive('analytics-button', analyticsDirectives.button);
Vue.directive('analytics-tab', analyticsDirectives.tab);

Vue.config.productionTip = false;

Vue.use(VeeValidate, veeConfig);
customValidationRules();
filters();

Vue.use(RxEvent);
Vue.use(VueMask);
Vue.use(VModal);
Vue.use(VueScrollTo);
Vue.use(VTooltip);
Vue.use(VueVisible);
Vue.use(PluginHead);

Vue.prototype.$message = Message;

@Component({
    mixins: [template],
    store,
    components: {
        Navbar,
        Sidebar,
        Foot,
        MessageModel,
    },
    router,
})
class App extends Vue {}

const app = new App().$mount('#app');

export { app };



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--1!./src/main.ts